import { Button, TextInput } from "@tremor/react";
import { API_BASE_URL } from "../consts.js";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/logo-primary.svg";
import {
  completeLoginChallenge,
  loginWithPassword,
  requestPasswordReset,
  resendVerification,
} from "../api/auth.js";
import { passkeysSupported, signInWithPasskey } from "../api/passkeys.js";
import { KeyRound } from "lucide-react";
import { acceptWorkspaceInvitation } from "../api/workspaces.js";
import { useAuthStore } from "../store/authStore.js";
import { fetchMe, fetchMyWorkspaces } from "../api/me.js";

import { useTranslation } from "react-i18next";

function getWorkspaceInvitationToken() {
  return (
    new URLSearchParams(window.location.search).get("workspace_invitation_token") ||
    sessionStorage.getItem("pending_invitation_token")
  );
}

function clearWorkspaceInvitationToken() {
  sessionStorage.removeItem("pending_invitation_token");
}

export default function Login() {
  const { t } = useTranslation("login");
  const navigate = useNavigate();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);
  const setSession = useAuthStore((s) => s.setSession);
  const setWorkspace = useAuthStore((s) => s.setWorkspace);
  const setWorkspaces = useAuthStore((s) => s.setWorkspaces);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(
    () => !!new URLSearchParams(window.location.search).get("access_token")
  );
  const [mode, setMode] = useState("login");
  const [challengeToken, setChallengeToken] = useState(null);
  const [challengeMethod, setChallengeMethod] = useState(null);
  const [challengeCode, setChallengeCode] = useState("");
  const [unverifiedEmail, setUnverifiedEmail] = useState(null);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const passkeyEnabled = passkeysSupported();

  useEffect(() => {
    async function completeGoogleLogin() {
      const params = new URLSearchParams(window.location.search);
      const accessToken = params.get("access_token");
      const oauthError = params.get("error");
      const workspaceInvitationToken = getWorkspaceInvitationToken();

      if (oauthError) {
        setGoogleLoading(false);
        setError(resolveGoogleError(oauthError, t));
        setInfo(null);
        window.history.replaceState({}, document.title, "/login");
        return;
      }

      if (!accessToken) {
        return;
      }

      setError(null);
      setInfo(null);
      setAccessToken(accessToken);

      try {
        const [orgs, me] = await Promise.all([
          fetchMyWorkspaces(),
          fetchMe().catch(() => null),
        ]);
        // Hydrate user so the next refresh can recover the session
        // (App.jsx bootstrap gates the silent-refresh attempt on persisted user).
        setSession({ access_token: accessToken, user: me ?? null, workspaces: orgs });

        if (workspaceInvitationToken) {
          setGoogleLoading(false);
          acceptPendingWorkspaceInvitation(accessToken, workspaceInvitationToken);
          return;
        }

        setGoogleLoading(false);
        window.history.replaceState({}, document.title, orgs.length > 1 ? "/pick-workspace" : "/dashboard");
        navigate(orgs.length > 1 ? "/pick-workspace" : "/dashboard", { replace: true });
      } catch {
        setGoogleLoading(false);
        navigate("/dashboard", { replace: true });
      }
    }

    completeGoogleLogin();
  }, [navigate, setAccessToken, t, setWorkspace, setWorkspaces]);

  async function acceptPendingWorkspaceInvitation(accessToken, token) {
    clearWorkspaceInvitationToken();
    try {
      setAccessToken(accessToken);
      await acceptWorkspaceInvitation(token);
      const orgs = await fetchMyWorkspaces();
      setWorkspaces(orgs);
      const dest = orgs.length > 1 ? "/pick-workspace" : "/dashboard";
      window.history.replaceState({}, document.title, dest);
      navigate(dest, { replace: true });
    } catch (err) {
      const message =
        err.response?.data?.error ?? t("errors.invite_accept_failed");
      setError(message);
      setInfo(null);
      window.history.replaceState({}, document.title, "/login");
    } finally {
      setGoogleLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      const data = await loginWithPassword(email, password);
      if (data?.requires_2fa) {
        setMode("challenge");
        setChallengeToken(data.challenge_token);
        setChallengeMethod(data.method);
        setChallengeCode("");
        setInfo(
          data.method === "email_otp"
            ? t("challenge.info_email", { email: data.email })
            : t("challenge.info_auth")
        );
        return;
      }

      setSession(data);

      const workspaceInvitationToken = getWorkspaceInvitationToken();
      if (workspaceInvitationToken) {
        await acceptPendingWorkspaceInvitation(data.access_token, workspaceInvitationToken);
        return;
      }

      const { workspace: activeWorkspace } = useAuthStore.getState();
      navigate(activeWorkspace ? "/dashboard" : "/pick-workspace", { replace: true });
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 403 && data?.data?.requires_email_verification) {
        setUnverifiedEmail(data.data.email ?? email);
        setMode("unverified");
        setError(null);
        setInfo(null);
        return;
      }
      const message = data?.error ?? t("errors.login_failed");
      setError(message);
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }


  async function handleResendVerification() {
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      await resendVerification(unverifiedEmail ?? email);
      setInfo(t("unverified.success_info"));
    } catch (err) {
      setError(err.response?.data?.error ?? t("errors.resend_failed"));
    } finally {
      setLoading(false);
    }
  }

  async function handleChallengeSubmit(e) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      const data = await completeLoginChallenge(challengeToken, challengeCode);
      setSession(data);

      const workspaceInvitationToken = getWorkspaceInvitationToken();
      if (workspaceInvitationToken) {
        await acceptPendingWorkspaceInvitation(data.access_token, workspaceInvitationToken);
        return;
      }

      const { workspace: activeWorkspace } = useAuthStore.getState();
      navigate(activeWorkspace ? "/dashboard" : "/pick-workspace", { replace: true });
    } catch (err) {
      setError(err.response?.data?.error ?? t("errors.verify_failed"));
    } finally {
      setLoading(false);
    }
  }

  async function handlePasskeyLogin() {
    setError(null);
    setInfo(null);
    setPasskeyLoading(true);
    try {
      const data = await signInWithPasskey(email || null);
      setSession(data);

      const workspaceInvitationToken = getWorkspaceInvitationToken();
      if (workspaceInvitationToken) {
        await acceptPendingWorkspaceInvitation(data.access_token, workspaceInvitationToken);
        return;
      }
      const { workspace: activeWorkspace } = useAuthStore.getState();
      navigate(activeWorkspace ? "/dashboard" : "/pick-workspace", { replace: true });
    } catch (err) {
      const isCancel =
        err?.name === "NotAllowedError" || err?.name === "AbortError";
      setError(isCancel ? t("errors.passkey_cancelled") : t("errors.passkey_failed"));
    } finally {
      setPasskeyLoading(false);
    }
  }

  async function handleForgotPassword(e) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      await requestPasswordReset(email);
      setInfo(t("forgot.success_info"));
    } catch (err) {
      setError(err.response?.data?.error ?? t("errors.reset_failed"));
    } finally {
      setLoading(false);
    }
  }

  function handleGoogleLogin() {
    setError(null);
    setGoogleLoading(true);
    const base = API_BASE_URL + "/auth/google/start";
    const workspaceInvitationToken = getWorkspaceInvitationToken();
    const url = workspaceInvitationToken
      ? `${base}?workspace_invitation_token=${encodeURIComponent(workspaceInvitationToken)}`
      : base;
    window.location.assign(url);
  }

  if (googleLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-sm text-gray-500">{t("signing_in")}</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm rounded-lg bg-white p-8 shadow">
        <div className="mb-6 flex flex-col items-center">
          <img src={logo} alt="Monitero" className="h-8 w-auto" />
        </div>

        {mode === "login" && (
          <>
            {passkeyEnabled && (
              <>
                <button
                  type="button"
                  onClick={handlePasskeyLogin}
                  disabled={passkeyLoading || loading || googleLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-teal-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:bg-teal-400"
                >
                  <KeyRound className="h-4 w-4" />
                  {passkeyLoading
                    ? t("login.passkey_authenticating")
                    : t("login.passkey")}
                </button>
                <p className="mt-2 text-center text-xs text-gray-500">
                  {t("login.passkey_hint")}
                </p>

                <div className="my-5 flex items-center gap-3">
                  <hr className="flex-1 border-gray-200" />
                  <span className="text-xs text-gray-400">{t("login.or")}</span>
                  <hr className="flex-1 border-gray-200" />
                </div>
              </>
            )}

            {(!passkeyEnabled || showPasswordForm) && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("login.email")}
                  </label>
                  <TextInput
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onValueChange={setEmail}
                    placeholder="you@example.com"
                    className="mt-1"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700"
                    >
                      {t("login.password")}
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setMode("forgot");
                        setError(null);
                        setInfo(null);
                      }}
                      className="text-xs font-medium text-teal-600 hover:text-teal-700"
                    >
                      {t("login.forgot_password")}
                    </button>
                  </div>
                  <TextInput
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onValueChange={setPassword}
                    placeholder="••••••••"
                    className="mt-1"
                  />
                </div>

                {error && (
                  <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
                    {error}
                  </p>
                )}
                {info && (
                  <p className="rounded-md bg-teal-50 px-3 py-2 text-sm text-teal-700">
                    {info}
                  </p>
                )}

                <Button type="submit" className="w-full" disabled={loading || googleLoading}>
                  {loading ? t("login.signing_in") : t("login.submit")}
                </Button>
              </form>
            )}

            {passkeyEnabled && !showPasswordForm && (
              <>
                {error && (
                  <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">
                    {error}
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(true);
                    setError(null);
                    setInfo(null);
                  }}
                  className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
                >
                  {t("login.use_password")}
                </button>
              </>
            )}

            <div className="my-3 flex items-center gap-3">
              <hr className="flex-1 border-gray-200" />
              <span className="text-xs text-gray-400">{t("login.or")}</span>
              <hr className="flex-1 border-gray-200" />
            </div>

            <button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading || googleLoading || passkeyLoading}
              className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400"
              title={t("login.google")}
            >
              <GoogleIcon />
              {googleLoading ? t("login.google_connecting") : t("login.google")}
            </button>
          </>
        )}

        {mode === "forgot" && (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t("login.email")}</label>
              <TextInput
                type="email"
                required
                value={email}
                onValueChange={setEmail}
                placeholder="you@example.com"
                className="mt-1"
              />
            </div>
            {error && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
            )}
            {info && (
              <p className="rounded-md bg-teal-50 px-3 py-2 text-sm text-teal-700">{info}</p>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t("forgot.sending") : t("forgot.submit")}
            </Button>
            <button
              type="button"
              onClick={() => {
                setMode("login");
                setError(null);
                setInfo(null);
              }}
              className="w-full text-sm font-medium text-gray-600 hover:text-gray-800"
            >
              {t("forgot.back_to_login")}
            </button>
          </form>
        )}

        {mode === "challenge" && (
          <form onSubmit={handleChallengeSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {challengeMethod === "email_otp" ? t("challenge.email_label") : t("challenge.auth_label")}
              </label>
              <TextInput
                required
                value={challengeCode}
                onValueChange={setChallengeCode}
                placeholder={challengeMethod === "email_otp" ? t("challenge.placeholder_email") : t("challenge.placeholder_auth")}
                className="mt-1"
              />
            </div>
            {error && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
            )}
            {info && (
              <p className="rounded-md bg-teal-50 px-3 py-2 text-sm text-teal-700">{info}</p>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t("challenge.verifying") : t("challenge.submit")}
            </Button>
          </form>
        )}


        {mode === "unverified" && (
          <div className="space-y-4 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
              <svg className="h-6 w-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-800">{t("unverified.title")}</p>
            <p className="text-sm text-gray-500">
              {t("unverified.description", { email: unverifiedEmail })}
            </p>
            {error && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
            )}
            {info && (
              <p className="rounded-md bg-teal-50 px-3 py-2 text-sm text-teal-700">{info}</p>
            )}
            <Button variant="secondary" className="w-full" disabled={loading} onClick={handleResendVerification}>
              {loading ? t("unverified.sending") : t("unverified.submit")}
            </Button>
            <button
              type="button"
              onClick={() => { setMode("login"); setError(null); setInfo(null); }}
              className="text-sm font-medium text-gray-600 hover:text-gray-800"
            >
              {t("unverified.back_to_login")}
            </button>
          </div>
        )}

        {mode === "login" && (
          <div className="mt-6 text-center text-sm text-gray-500">
            {t("login.no_account")}{" "}
            <Link to="/signup" className="font-medium text-teal-600 hover:text-teal-700">
              {t("login.sign_up")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function resolveGoogleError(errorCode, t) {
  if (errorCode === "google_oauth_denied") {
    return t("errors.google_denied");
  }

  if (errorCode === "google_oauth_state") {
    return t("errors.google_state");
  }

  return t("errors.google_failed");
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}
